import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailPlus, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function InvitePage() {
  const { setUser } = useAuth();
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== password2) {
      toast.error(t("Şifreler eşleşmiyor"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name, password });
      setUser(data);
      toast.success(t("Hesabınız oluşturuldu. Hoş geldiniz!"));
      navigate("/panel", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 auth-noise" data-testid="invite-page">
      <header className="p-8">
        <Link to="/login"><Wordmark /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-10 fade-up">
          {!token ? (
            <div data-testid="invite-no-token">
              <XCircle className="h-12 w-12 text-red-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Geçersiz davet")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("Davet bağlantısı geçersiz veya süresi dolmuş.")}
              </p>
              <Button asChild className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/login" data-testid="invite-goto-login-button">{t("Giriş sayfasına dön")}</Link>
              </Button>
            </div>
          ) : (
            <div data-testid="invite-form-wrap">
              <MailPlus className="h-12 w-12 text-blue-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Daveti Kabul Et")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("Firma hesabına katılmak için adını ve şifreni belirle.")}
              </p>
              <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="invite-form">
                <div className="space-y-2">
                  <Label htmlFor="invite-name">{t("Ad Soyad")}</Label>
                  <Input id="invite-name" required minLength={2} value={name}
                    onChange={(e) => setName(e.target.value)} autoComplete="name"
                    data-testid="invite-name-input" className="focus-visible:ring-blue-600" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-password">{t("Şifre")}</Label>
                  <Input id="invite-password" type="password" required minLength={8} value={password}
                    onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                    placeholder="••••••••" data-testid="invite-password-input"
                    className="focus-visible:ring-blue-600" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-password2">{t("Şifre (Tekrar)")}</Label>
                  <Input id="invite-password2" type="password" required minLength={8} value={password2}
                    onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password"
                    placeholder="••••••••" data-testid="invite-password2-input"
                    className="focus-visible:ring-blue-600" />
                </div>
                <Button type="submit" disabled={saving} data-testid="invite-submit-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Hesabı Oluştur")}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
